// Formula strings shown next to each result (stat cards): the EC5/ETA formula actually used, with the current numbers
// substituted. HTML (sub/sup) built only from numbers and constants; pure formatting of values already computed by the
// engine. The PDF report writes its own, in its language (internal/export/screw_connector/formulas.go).
import { fmtN, foldAcuteAngle } from "../../../http/screwConnector/formulas/angles";
import { capNote, kefLookup } from "../../../http/screwConnector/formulas/resistance";
import type { CalcState } from "../../../http/screwConnector/formulas/state";
import type { EngineTexts } from "../../../http/screwConnector/formulas/ctx";

export { fmtN };

type Resistenza = CalcState["resistenza"];
type Num = number | null | undefined;

const fix = (x: Num, dec: number): string => (typeof x === "number" ? x.toFixed(dec) : "NaN");

// --- Fv,Rk per screw ---

// Governing mode only (results card): concise, language-neutral formula.
export function formulaFvRkVite(resistenza: Resistenza, t: EngineTexts): string {
  const v = resistenza.vars;
  if (resistenza.double) {
    const planes = resistenza.planes;
    if (!planes) return `F<sub>v,Rk</sub> = ${fmtN(resistenza.FvRk, 0)} N`;
    const p1 = planes[0],
      p2 = planes[1];
    return `F<sub>v,Rk</sub> = min(F<sub>v,Rk</sub>${p1?.label ?? ""}; F<sub>v,Rk</sub>${p2?.label ?? ""}) = min(${fmtN(p1?.FvRk, 0)}; ${fmtN(p2?.FvRk, 0)}) = ${fmtN(resistenza.FvRk, 0)} N`;
  }
  if (v.tPlate !== undefined) {
    // steel-to-timber (plate = element 1), EC5 §8.2.3
    const d = fmtN(v.d, 1),
      tWood = fmtN(v.tWood, 1),
      fhk = fmtN(v.fhk, 2);
    const m = resistenza.modes;
    const thinLabel =
      resistenza.thinMode === "a"
        ? `0.4·f<sub>h,k</sub>·t<sub>wood</sub>·d = 0.4·${fhk}·${tWood}·${d} = ${fmtN(m["thinA"], 0)} N`
        : `1.15·√(2·M<sub>y</sub>·f<sub>h,k</sub>·d) + F<sub>ax</sub>/4 = ${fmtN(m["thinB"], 0)} N`;
    const thickLabel =
      resistenza.thickMode === "c"
        ? `f<sub>h,k</sub>·t<sub>wood</sub>·d = ${fhk}·${tWood}·${d} = ${fmtN(m["thickC"], 0)} N`
        : resistenza.thickMode === "d"
          ? `f<sub>h,k</sub>·t<sub>wood</sub>·d·(√(2+4M<sub>y</sub>/(f<sub>h,k</sub>·d·t<sub>wood</sub>²))−1) + F<sub>ax</sub>/4 = ${fmtN(m["thickD"], 0)} N`
          : `2.3·√(M<sub>y</sub>·f<sub>h,k</sub>·d) + F<sub>ax</sub>/4 = ${fmtN(m["thickE"], 0)} N`;
    if (resistenza.branch === "thin") return `EC5 8.2.3: F<sub>v,Rk</sub> = ${thinLabel}${capNote(resistenza, resistenza.thinMode ?? "b", t)}`;
    if (resistenza.branch === "thick") return `EC5 8.2.3: F<sub>v,Rk</sub> = ${thickLabel}${capNote(resistenza, resistenza.thickMode ?? "c", t)}`;
    const frac = fmtN(resistenza.frac, 3);
    return `EC5 8.2.3: F<sub>v,Rk</sub> = F<sub>v,Rk,thin</sub> + f·(F<sub>v,Rk,thick</sub>−F<sub>v,Rk,thin</sub>) = ${fmtN(resistenza.thin, 0)} + ${frac}·(${fmtN(resistenza.thick, 0)}−${fmtN(resistenza.thin, 0)}) = ${fmtN(resistenza.FvRk, 0)} N`;
  }
  // timber-to-timber, EC5 eq. 8.6 a-f (Johansen)
  const templates = singleShearTemplates(resistenza);
  const mode = resistenza.mode ?? "";
  return `EC5 8.6${mode}: ${templates[mode] ?? ""}${capNote(resistenza, mode, t)}`;
}

function singleShearTemplates(resistenza: Resistenza): Record<string, string> {
  const v = resistenza.vars;
  const d = fmtN(v.d, 1),
    t1 = fmtN(v.t1, 1),
    t2 = fmtN(v.t2, 1);
  const fh1k = fmtN(v.fh1k, 2),
    fh2k = fmtN(v.fh2k, 2),
    My = fmtN(v.myRk, 0),
    beta = fmtN(v.beta, 3);
  return {
    a: `F<sub>v,Rk</sub> = f<sub>h1,k</sub>·t1·d — con f<sub>h1,k</sub>=${fh1k}, t1=${t1}, d=${d}`,
    b: `F<sub>v,Rk</sub> = f<sub>h2,k</sub>·t2·d — con f<sub>h2,k</sub>=${fh2k}, t2=${t2}, d=${d}`,
    c: `F<sub>v,Rk</sub> = [f<sub>h1,k</sub>·t1·d/(1+β)]·[√(β+2β²(1+t2/t1+(t2/t1)²)+β³(t2/t1)²) − β(1+t2/t1)] + F<sub>ax</sub>/4 — con f<sub>h1,k</sub>=${fh1k}, f<sub>h2,k</sub>=${fh2k} (β=${beta}), t1=${t1}, t2=${t2}, d=${d}`,
    d: `F<sub>v,Rk</sub> = 1,05·[f<sub>h1,k</sub>·t1·d/(2+β)]·[√(2β(1+β)+4β(2+β)M<sub>y</sub>/(f<sub>h1,k</sub>·d·t1²)) − β] + F<sub>ax</sub>/4 — con f<sub>h1,k</sub>=${fh1k} (β=${beta}), t1=${t1}, d=${d}, M<sub>y</sub>=${My}`,
    e: `F<sub>v,Rk</sub> = 1,05·[f<sub>h1,k</sub>·t2·d/(1+2β)]·[√(2β²(1+β)+4β(1+2β)M<sub>y</sub>/(f<sub>h1,k</sub>·d·t2²)) − β] + F<sub>ax</sub>/4 — con f<sub>h1,k</sub>=${fh1k} (β=${beta}), t2=${t2}, d=${d}, M<sub>y</sub>=${My}`,
    f: `F<sub>v,Rk</sub> = 1,15·√(2β/(1+β))·√(2·M<sub>y</sub>·f<sub>h1,k</sub>·d) + F<sub>ax</sub>/4 — con β=${beta}, M<sub>y</sub>=${My}, f<sub>h1,k</sub>=${fh1k}, d=${d}`,
  };
}

// --- effective number of screws ---

export function formulaNef(state: CalcState, t: EngineTexts): string {
  const n = state.viteAmmissibili;
  const p = t.formulas;
  if (state.sfalsaViti) return p.staggerNef(n);
  return p.nefAxial(n, Math.pow(n, 0.9).toFixed(2), (0.9 * n).toFixed(2), fix(state.nef, 2));
}

// Shear n_ef, EC5 §8.3.1.2(4) eq. (8.17), detailed for the governing element.
export function formulaNefShear(state: CalcState, t: EngineTexts): string {
  const p = t.formulas;
  if (state.sfalsaViti) return p.staggerNefShear(state.viteAmmissibili);
  const governElem = state.nefShearGovernElem;
  const nRiga = governElem === 1 ? state.rows : state.cols;
  const nRipetizioni = governElem === 1 ? state.cols : state.rows;
  const a1 = governElem === 1 ? state.s11 : state.s12;
  const steelToTimber = governElem === 2 && state.materiale1 === "acciaio";
  const a1Eff = steelToTimber ? a1 / 0.7 : a1;
  const kef = kefLookup(a1, state.d, state.predrilled, steelToTimber);
  // no valid diameter selected: kef cannot be looked up
  if (kef == null) return p.noDiameter;
  const nef0 = Math.pow(nRiga, kef);
  const angleGrainDeg = governElem === 1 ? state.angleGrainElem1 : state.angleGrainElem2;
  const angoloAcuto = foldAcuteAngle(angleGrainDeg);
  const nRigaInterpolato = nef0 + (nRiga - nef0) * (angoloAcuto / 90);
  const crit =
    state.materiale1 === "acciaio" || state.panel1 ? "" : p.critElement(governElem);
  const a1TableNote = steelToTimber
    ? p.table81Steel(fmtN(a1Eff, 0), (a1Eff / state.d).toFixed(1))
    : "";
  const panelNote = state.panel1 ? p.panelNoInterp : "";
  return (
    p.rowParallel(nRiga, fmtN(a1, 0), (a1 / state.d).toFixed(1), governElem, nRipetizioni, a1TableNote) +
    `<br>` +
    p.kefLine(kef.toFixed(2), nRiga, nef0.toFixed(2)) +
    `<br>` +
    p.angleLine(angoloAcuto.toFixed(1), panelNote, nef0.toFixed(2), nRiga, nRigaInterpolato.toFixed(2)) +
    `<br>` +
    p.totalShear(nRipetizioni, nRigaInterpolato.toFixed(2), crit) +
    ` = ${fix(state.nefShear, 2)}`
  );
}

// --- connection resistances ---

export function formulaFvRkUnione(state: CalcState, t: EngineTexts): string {
  const r = state.resistenza;
  const p = t.formulas;
  if (state.threeD)
    return `F<sub>v,Rk</sub> = n<sub>p</sub> · n<sub>ef</sub> · F<sub>v,Rk</sub>(${r.govLabel || p.govPlane}) = ${state.nPiani} × ${fix(state.nefShear, 2)} × ${fix(r.FvRk, 0)} = ${fix(state.FvRkConnessione, 0)} N (${p.doubleNote})`;
  if (state.steel3)
    return `F<sub>v,Rk</sub> = n<sub>c</sub> · n<sub>ef</sub> · F<sub>v,Rk</sub> = ${state.nPiani} × ${fix(state.nefShear, 2)} × ${fix(r.FvRk, 0)} = ${fix(state.FvRkConnessione, 0)} N (${p.steelNote(state.panel1 ? p.sidePanels : p.sidePlates)})`;
  if (state.three)
    return `F<sub>v,Rk</sub> = n<sub>c</sub> · n<sub>ef</sub> · F<sub>v,Rk</sub> = ${state.nPiani} × ${fix(state.nefShear, 2)} × ${fix(r.FvRk, 0)} = ${fix(state.FvRkConnessione, 0)} N (${p.threeNote})`;
  return `F<sub>v,Rk</sub> = n<sub>ef</sub> · F<sub>v,Rk</sub> = ${fix(state.nefShear, 2)} × ${fix(r.FvRk, 0)} = ${fix(state.FvRkConnessione, 0)} N`;
}

export function formulaForceF(state: CalcState): string {
  return `F<sub>v,Ed</sub> = √(F<sub>n</sub>² + F<sub>v</sub>²) = √(${fix(state.fnKN, 2)}² + ${fix(state.fvKN, 2)}²) = ${fix(state.forceF_kN, 2)} kN`;
}

export function formulaFaxRdUnione(state: CalcState): string {
  return `F<sub>ax,Rd</sub> = k<sub>mod</sub>·F<sub>ax,Rk</sub>/γ<sub>M</sub> = (${fix(state.kmod, 2)}×${fix(state.FaxRkUnione, 0)})/${fix(state.gammaM, 2)} = ${fix(state.FaxRdUnione, 0)} N`;
}

// EC5 §8.3.3(1) eq. (8.28): η = (Fax,Ed/Fax,Rd)² + (Fv,Ed/(n·Fv,Rd))².
export function formulaCaricoCombinato(state: CalcState): string {
  const nTot = (state.nPiani || 1) * state.viteAmmissibili;
  const t1 = state.faKN / (state.FaxRdUnione / 1000);
  const t2 = state.forceF_kN / (nTot * (state.FvRdVite / 1000));
  const nTxt = state.three || state.steel3 ? `${state.nPiani}×${state.viteAmmissibili}` : `${state.viteAmmissibili}`;
  return `η = (F<sub>ax,Ed</sub>/F<sub>ax,Rd</sub>)² + (F<sub>v,Ed</sub>/(n·F<sub>v,Rd</sub>))² = (${fix(state.faKN, 2)}/${(state.FaxRdUnione / 1000).toFixed(2)})² + (${fix(state.forceF_kN, 2)}/(${nTxt}×${(state.FvRdVite / 1000).toFixed(2)}))² = ${t1.toFixed(3)}² + ${t2.toFixed(3)}² = ${((state.etaCombinata ?? NaN) * 100).toFixed(2)} %`;
}

export function formulaKser(state: CalcState, t: EngineTexts): string {
  const kN = (v: number) => (v / 1000).toFixed(2);
  const kser = state.kser;
  const p = t.formulas;
  if (kser == null) return p.kserNoRho;
  const rhoM = state.kserRhoM ?? NaN;
  if (state.kserBranch === "lvl") {
    return `K<sub>ser</sub> = 60·(d·ρ<sub>mean</sub>/510)<sup>1.5</sup> = 60·(${fix(state.d, 1)}×${rhoM.toFixed(0)}/510)<sup>1.5</sup> = ${kser.toFixed(0)} N/mm = ${kN(kser)} kN/mm${p.kserLvlNote(state.screwRec?.family ?? "")}`;
  }
  const predrilled = state.predrilled ? p.kserPredrilled : p.kserNotPredrilled;
  const rhoMTxt = state.panel1
    ? p.kserPanelRho(fix(state.rhok1, 0), fix(state.rhomean2, 0), rhoM.toFixed(0))
    : state.materiale1 === "acciaio"
      ? p.kserMean2(rhoM.toFixed(0))
      : p.kserTimberRho(fix(state.rhomean1, 0), fix(state.rhomean2, 0), rhoM.toFixed(0));
  const formulaTxt = state.predrilled
    ? `ρ<sub>m</sub><sup>1.5</sup>·d/23 = ${rhoM.toFixed(0)}<sup>1.5</sup>×${fix(state.d, 1)}/23`
    : `ρ<sub>m</sub><sup>1.5</sup>·d<sup>0.8</sup>/30 = ${rhoM.toFixed(0)}<sup>1.5</sup>×${fix(state.d, 1)}<sup>0.8</sup>/30`;
  if (state.materiale1 === "acciaio") {
    const base = kser / 2;
    return `K<sub>ser,base</sub> = ${formulaTxt} = ${base.toFixed(0)} N/mm<br>K<sub>ser</sub> = K<sub>ser,base</sub>×2.0 = ${base.toFixed(0)}×2.0 = ${kser.toFixed(0)} N/mm = ${kN(kser)} kN/mm<span class="report-formula">${p.kserSteelNote(predrilled, rhoMTxt)}`;
  }
  return `K<sub>ser</sub> = ${formulaTxt} = ${kser.toFixed(0)} N/mm = ${kN(kser)} kN/mm<span class="report-formula">${p.kserTimberNote(predrilled, rhoMTxt)}`;
}

export function formulaFvRdUnione(state: CalcState): string {
  return `F<sub>v,Rd</sub> = k<sub>mod</sub>·F<sub>v,Rk</sub>/γ<sub>M</sub> = (${fix(state.kmod, 2)}×${fix(state.FvRkConnessione, 0)})/${fix(state.gammaM, 2)} = ${fix(state.FvRdUnione, 0)} N ≈ ${(state.FvRdUnione / 1000).toFixed(2)} kN`;
}
